import { useMemo } from 'react'
import { useGameStore } from '@/store/gameStore'
import { CLUB_LEVELS } from '@/config/clubLevelConfig'
import { PLAYER_CLUB_ID } from '@/config/leagueConfig'

export interface NewsItem {
  id: string
  headline: string
  category: 'economy' | 'weather' | 'staff' | 'facilities' | 'tournament' | 'milestone' | 'effect' | 'story'
  /** When set, the feed shows this exact author + verbatim headline (story beats) */
  authorId?: string
}

export function useClubNews(): NewsItem[] {
  const transactions = useGameStore(s => s.transactions)
  const currentWeather = useGameStore(s => s.currentWeather)
  const activeEffects = useGameStore(s => s.activeEffects)
  const courts = useGameStore(s => s.courts)
  const staff = useGameStore(s => s.staff)
  const clubLevel = useGameStore(s => s.clubLevel)
  const activeTournament = useGameStore(s => s.activeTournament)
  const league = useGameStore(s => s.league)
  const storyNews = useGameStore(s => s.storyNews)
  const day = useGameStore(s => s.day)

  return useMemo(() => {
    const news: NewsItem[] = []

    // Narrative posts (StorySystem) lead the feed — they're the freshest voice
    news.push(...storyNews)

    // Recent income transactions (last 5 days)
    const recentIncome = transactions
      .filter(t => t.type === 'income' && t.day >= day - 5)
    if (recentIncome.length > 0) {
      const totalRecent = recentIncome.reduce((sum, t) => sum + t.amount, 0)
      news.push({
        id: 'recent-income',
        headline: `Las instalaciones generan ${totalRecent.toLocaleString('es-ES')}\u20AC en los ultimos dias`,
        category: 'economy',
      })
    }

    // Weather effects
    if (currentWeather === 'rainy' || currentWeather === 'stormy') {
      const outdoorCount = courts.filter(c => c.type === 'outdoor').length
      if (outdoorCount > 0) {
        news.push({
          id: 'weather-outdoor',
          headline: `Lluvia: ${outdoorCount} pistas outdoor con actividad reducida`,
          category: 'weather',
        })
      }
    }

    if (currentWeather === 'sunny') {
      news.push({
        id: 'weather-sunny',
        headline: 'Buen tiempo: las pistas outdoor estan a pleno rendimiento',
        category: 'weather',
      })
    }

    // Active effects
    for (const effect of activeEffects) {
      const daysLeft = effect.expiresOnDay - day
      if (daysLeft > 0) {
        news.push({
          id: `effect-${effect.id}`,
          headline: `Efecto activo: ${effect.id} (${daysLeft} dias restantes)`,
          category: 'effect',
        })
      }
    }

    // Court conditions
    const lowCourts = courts.filter(c => c.condition < 30)
    if (lowCourts.length > 0) {
      news.push({
        id: 'court-condition',
        headline: `${lowCourts.length} pista${lowCourts.length > 1 ? 's' : ''} necesita${lowCourts.length > 1 ? 'n' : ''} mantenimiento urgente`,
        category: 'facilities',
      })
    }

    // Staff hired
    if (staff.length > 0) {
      const lastStaff = staff[staff.length - 1]
      news.push({
        id: 'staff-latest',
        headline: `${lastStaff.type} contratado en plantilla (${lastStaff.salary}\u20AC/sem)`,
        category: 'staff',
      })
    }

    // Active tournament
    if (activeTournament) {
      news.push({
        id: 'tournament-active',
        headline: `Torneo "${activeTournament.name}" en curso`,
        category: 'tournament',
      })
    }

    // League: last result and next rival
    if (league) {
      const played = league.fixtures
        .filter(f => f.result && (f.homeId === PLAYER_CLUB_ID || f.awayId === PLAYER_CLUB_ID))
        .sort((a, b) => b.matchday - a.matchday)
      const last = played[0]
      if (last) {
        const won = last.result!.winnerId === PLAYER_CLUB_ID
        const rivalId = last.homeId === PLAYER_CLUB_ID ? last.awayId : last.homeId
        const rival = league.clubs.find(c => c.id === rivalId)?.name ?? 'Rival'
        news.push({
          id: `league-result-${last.matchday}`,
          headline: won
            ? `Liga J${last.matchday}: victoria ${last.result!.homeMatchWins}-${last.result!.awayMatchWins} contra ${rival}`
            : `Liga J${last.matchday}: derrota contra ${rival}`,
          category: 'tournament',
        })
      }
      const next = league.fixtures.find(
        f => !f.result && (f.homeId === PLAYER_CLUB_ID || f.awayId === PLAYER_CLUB_ID)
      )
      if (next) {
        const rivalId = next.homeId === PLAYER_CLUB_ID ? next.awayId : next.homeId
        const rival = league.clubs.find(c => c.id === rivalId)?.name ?? 'Rival'
        news.push({
          id: `league-next-${next.matchday}`,
          headline: `Proxima jornada de liga (J${next.matchday}): contra ${rival}`,
          category: 'tournament',
        })
      }
    }

    // Level milestone
    const levelInfo = CLUB_LEVELS.find(l => l.level === clubLevel)
    if (levelInfo && clubLevel > 0) {
      news.push({
        id: 'level-milestone',
        headline: `Club alcanza nivel ${clubLevel}: "${levelInfo.name}"`,
        category: 'milestone',
      })
    }

    // If no news, add a placeholder
    if (news.length === 0) {
      news.push({
        id: 'no-news',
        headline: 'Sin novedades por ahora. Construye tu primera pista para empezar.',
        category: 'milestone',
      })
    }

    return news.slice(0, 8)
  }, [transactions, currentWeather, activeEffects, courts, staff, clubLevel, activeTournament, league, storyNews, day])
}
